import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import {
  Collapsible,
  useBreakpoint,
  useCollapsibleState,
} from '@techstyle/react-components';
import { FormattedHTMLMessage, FormattedMessage } from '@techstyle/react-intl';

import { mobile, desktop } from '../../styles';
import { isPreorderable as isPreorderableSelector } from '../../utils/selectors';
import { Component as ByoProvider } from '../ByoProvider';
import {
  Component as CuralateExperience,
  CurateFilterType,
} from '../CuralateExperience';
import { Component as ProductDescription } from '../ProductDescription';
import { Component as StaticIcon } from '../StaticIcon';

const DescriptionWrapper = styled.div`
  ${mobile`
    order: 4;
    padding-top: 0;
  `};

  padding-top: ${props => props.theme.spacing.medium}px;

  ${desktop`
    padding-left: 24px;
    padding-right: 24px;
  `};

  @media (min-width: 1024px) {
    padding-left: 115px;
  }

  @media (min-width: 1440px) {
    padding-left: 136px;
  }

  @media (min-width: 1024px), (min-width: 1440px) {
    padding-right: 0;
    padding-left: 130px;
  }
`;

const StyledProductDescription = styled(ProductDescription)`
  padding: ${props => props.theme.spacing.tiny}px
    ${props => props.theme.spacing.medium}px
    ${props => props.theme.spacing.moderate}px;

  ${mobile`padding-bottom: 0;`}

  @media (min-width: ${({ theme }) =>
    theme.breakpoints.large}px) and (max-width: ${({ theme }) =>
    theme.breakpoints.xxlarge}px) {
    padding-left: 56px;
  }
`;

const ProductShippingWrapper = styled.div`
  padding: 8px ${props => props.theme.spacing.medium}px
    ${props => props.theme.spacing.moderate}px;
`;

const CollapsibleMobileDetails = styled.div`
  order: 3;
`;

const StyledCollapsible = styled(Collapsible)``;

const CollapsibleContent = styled(Collapsible.Content)`
  &[aria-hidden='true'] {
    padding: 0;
    border: none;
  }
`;

const StyledCollapsibleButton = styled(Collapsible.Button)`
  ${props => props.theme.collapsibleTrigger.defaultStyle};
  border-top: 1px solid ${props => props.theme.colors.default};
  padding: ${props => props.theme.spacing.tiny}px
    ${props => props.theme.spacing.medium}px;

  ${({ theme }) => theme.paragraph.variants.paragraph3Uppercase.textStyles}

  ${props =>
    (props.isOpen || props.context === 'details') &&
    css`
      border-bottom-style: none;
    `}

  &:focus {
    outline: none;
  }
`;

const CuralateExperienceWrapper = styled.div`
  ${mobile`padding-bottom: ${props => props.theme.spacing.large}px;`};
`;

const DownStaticIcon = styled(StaticIcon).attrs({
  type: 'caret',
  size: 8,
})`
  margin-left: auto;
  color: ${props => props.theme.colors.default};
`;

const UpStaticIcon = styled(DownStaticIcon)`
  transform: rotate(180deg);
`;

const Wrapper = styled.div``;

function ToggleButton({ children, context = '' }) {
  const { isOpen } = useCollapsibleState();

  return (
    <StyledCollapsibleButton isOpen={isOpen} context={context}>
      {children}
      {isOpen ? <UpStaticIcon /> : <DownStaticIcon />}
    </StyledCollapsibleButton>
  );
}

ToggleButton.propTypes = {
  children: PropTypes.node,
  context: PropTypes.string,
};

function ProductDetailDescription({
  className,
  product,
  isGwpQuickView = false,
  isProductSet = false,
  shouldShowShippingExchange = true,
}) {
  const { isMobile, isDesktop } = useBreakpoint();
  const { long_description: longDescription } = product;
  const { isBorderfreeCustomer } = useSelector(state => state.borderfree);
  const { isPreorderable } = useSelector(state => ({
    isPreorderable: isPreorderableSelector(state, { product }),
  }));

  const productKey = useMemo(() => {
    const groupCodes = [product.group_code];

    if (Array.isArray(product.linked_master_product)) {
      product.linked_master_product.forEach(linkedProduct => {
        groupCodes.push(linkedProduct.group_code);
      });
    }

    return groupCodes.sort().join('-');
  }, [product]);

  if (isProductSet) {
    return isPreorderable ? null : (
      <Wrapper className={className}>
        <DescriptionWrapper>
          <CuralateExperienceWrapper>
            <CuralateExperience
              experienceName="custom-product"
              filterType={CurateFilterType.PRODUCT_ID}
              filterText={product.master_product_id}
              destroyOnUnmount={false}
              isPdp
            />
          </CuralateExperienceWrapper>
        </DescriptionWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper className={className}>
      <DescriptionWrapper>
        {isDesktop && isGwpQuickView ? (
          <CollapsibleMobileDetails>
            <StyledCollapsible defaultOpen>
              <ToggleButton context="details">
                <div data-autotag="mobile-details">
                  <FormattedMessage
                    id="site_pdp.product_details_title"
                    defaultMessage="Details"
                  />
                </div>
              </ToggleButton>
              <CollapsibleContent>
                <StyledProductDescription
                  tabs={false}
                  details={
                    product.medium_description ? product.medium_description : ''
                  }
                  isBorderfreeCustomer={isBorderfreeCustomer}
                  longDescription={longDescription}
                  shouldShowShippingExchange={false}
                  isGwpQuickView={isGwpQuickView}
                />
              </CollapsibleContent>
            </StyledCollapsible>
          </CollapsibleMobileDetails>
        ) : null}
        {isDesktop && !isGwpQuickView ? (
          <StyledProductDescription
            tabs
            shouldShowShippingExchange={shouldShowShippingExchange}
            details={
              product.medium_description ? product.medium_description : ''
            }
            longDescription={longDescription}
          />
        ) : null}

        {/* Hides UGC section on PDP while in Build Your Own Set flow or for a Pre-Order product */}
        {!isPreorderable && !isGwpQuickView ? (
          <ByoProvider.Consumer>
            {({ isFlowInitiated }) =>
              isFlowInitiated ? null : (
                <CuralateExperienceWrapper data-testid="curalate-experience">
                  <CuralateExperience
                    experienceName="custom-product"
                    filterType={CurateFilterType.PRODUCT_ID}
                    filterText={product.master_product_id}
                    destroyOnUnmount={false}
                    isPdp
                  />
                </CuralateExperienceWrapper>
              )
            }
          </ByoProvider.Consumer>
        ) : null}
      </DescriptionWrapper>
      {isMobile ? (
        <CollapsibleMobileDetails>
          {shouldShowShippingExchange && !isBorderfreeCustomer ? (
            <StyledCollapsible defaultOpen={false}>
              <ToggleButton>
                <div data-autotag="mobile-delivery">
                  <FormattedMessage
                    id="site_pdp.shipping_exchanges_returns"
                    defaultMessage="Shipping, Exchanges & Returns"
                  />
                </div>
              </ToggleButton>
              <CollapsibleContent>
                <ProductShippingWrapper>
                  <FormattedHTMLMessage
                    id="site_pdp.shipping_return_guarantee_html"
                    defaultMessage="If you’re not completely satisfied with your SavageX.com or Savage X Fenty retail store purchase, Rewards Members can return their items for free within 90 days of purchase (30 days for Guests). Both online and in-store purchases may be returned in-store as long as all original tags are attached. 

Any item to be returned or exchanged must be in new, unused and resalable condition, with original tags attached. Any return or exchange should be sent back in the original, undamaged box and packaging, plus any accessories or extras that may have been included. Exchanges are subject to size and style availability. 

Please note: All Sale items are final sale and are not eligible for returns or exchanges. Exclusions and limitations apply."
                  />
                </ProductShippingWrapper>
              </CollapsibleContent>
            </StyledCollapsible>
          ) : null}
          <StyledCollapsible defaultOpen={false} key={productKey}>
            <ToggleButton context="details">
              <div data-autotag="mobile-details">
                <FormattedMessage
                  id="site_pdp.product_details_title"
                  defaultMessage="Details"
                />
              </div>
            </ToggleButton>
            <CollapsibleContent>
              <StyledProductDescription
                tabs={false}
                details={
                  product.medium_description ? product.medium_description : ''
                }
                isBorderfreeCustomer={isBorderfreeCustomer}
                longDescription={longDescription}
              />
            </CollapsibleContent>
          </StyledCollapsible>
        </CollapsibleMobileDetails>
      ) : null}
    </Wrapper>
  );
}

ProductDetailDescription.DescriptionWrapper = DescriptionWrapper;
ProductDetailDescription.StyledProductDescription = StyledProductDescription;
ProductDetailDescription.StyledCollapsibleButton = StyledCollapsibleButton;

ProductDetailDescription.propTypes = {
  className: PropTypes.string,
  product: PropTypes.object.isRequired,
  isGwpQuickView: PropTypes.bool,
  isProductSet: PropTypes.bool,
  shouldShowShippingExchange: PropTypes.bool,
};

export default ProductDetailDescription;
